import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { getCurrentCompanyId } from '../lib/company';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HandCoins, Bell } from 'lucide-react';
import { money } from '../lib/share';
import InstallmentRow from '../components/InstallmentRow';

export default function Collections() {
  const { user } = useAuth();
  const [day, setDay] = useState(new Date().toLocaleDateString('en-CA'));
  const [data, setData] = useState(null);
  const canPay = ['master', 'superadmin', 'admin'].includes(user?.role_id);
  const load = async () => {
    try { setData((await api.getCollections({ day, company_id: getCurrentCompanyId() || undefined })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al cargar cobranza'); }
  };
  useEffect(() => { load(); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (it, amount, reference) => {
    if (!amount || amount <= 0) return toast.error('SalesPRO: Monto inválido');
    try { await api.payInstallment(it.installment_id, { amount, method_name: reference ? 'Cobranza' : null, reference }); toast.success('SalesPRO: Pago registrado y notificado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al registrar pago'); }
  };
  const notify = async (ids, channels) => {
    try {
      const r = (await api.notifyCollections({ installment_ids: ids, channels })).data;
      toast.success(`SalesPRO: ${r.internal} notificación(es) interna(s), ${r.emails} correo(s)${r.email_error === 'no_smtp' ? ' (correo no configurado)' : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al notificar'); }
  };
  const tabs = [
    { key: 'today', label: 'Cuotas del día', color: 'text-blue-700' },
    { key: 'overdue', label: 'Vencidas (arrastre)', color: 'text-red-700' },
    { key: 'upcoming', label: 'Próximos 7 días', color: 'text-amber-700' },
  ];

  return (
    <div className="space-y-4" data-testid="collections-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black text-[#022C22] flex items-center gap-2"><HandCoins /> Cobranza</h1>
          <p className="text-sm text-[#64748B]">Lista de pagos del día, cuotas vencidas y contactos de referencia para gestionar el cobro.</p>
        </div>
        <div className="flex gap-2 items-end">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="collections-date" className="w-44" />
          {data && <Button variant="outline" data-testid="notify-all-overdue" onClick={() => notify([...data.today, ...data.overdue].map((i) => i.installment_id), ['internal', 'email'])}><Bell size={14} className="mr-1" /> Notificar hoy + vencidas</Button>}
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3">
          {tabs.map((t) => (
            <div key={t.key} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`collections-total-${t.key}`}>
              <p className="text-xs text-[#64748B]">{t.label}</p>
              <p className={`font-heading text-2xl font-black ${t.color}`}>{money(data.totals[t.key])}</p>
              <p className="text-xs text-[#94A3B8]">{data.totals[`${t.key}_count`]} cuota(s)</p>
            </div>
          ))}
        </div>
      )}
      {data && (
        <Tabs defaultValue="today">
          <TabsList>{tabs.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`collections-tab-${t.key}`}>{t.label} ({data[t.key].length})</TabsTrigger>)}</TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.key} value={t.key} className="space-y-2">
              {data[t.key].length === 0 ? <p className="text-sm text-[#94A3B8] py-8 text-center">Sin cuotas en esta lista.</p>
                : data[t.key].map((it) => <InstallmentRow key={it.installment_id} it={it} onPay={pay} onNotify={notify} canPay={canPay} />)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
